import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import includes from "lodash/includes";

import {
  Field,
  HTMLWrapper,
  Input,
  useBoolean,
  IconButton,
  InputGroup,
  VStack,
  VisuallyHidden
} from "@/storybook-components";
import type { ChangeEvent } from "react";
import type { Question } from "@/types";

import { setAnswer, setError } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface PasswordQuestionProps {
  question: Partial<Question>;
}

export const validate = async (answer: string): Promise<string[]> => {
  const schema = string()
    .required()
    .min(8)
    .max(16)
    .test("isValid", "invalidPassword", () =>
      /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).+/.test(answer)
    )
    .test("confirmPassword", "confirmPasswordMismatch", () => {
      const field =
        document.querySelector<HTMLInputElement>("#confirmPassword");

      return field.value.length ? field.value === answer : false;
    });

  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const PasswordQuestion = ({ question }: PasswordQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const [revealPwd, setRevealPwd] = useBoolean();
  const [revealConfirmPwd, setRevealConfirmPwd] = useBoolean();

  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const passwordError = (errors[question.id] || []) as any;
  const confirmPasswordError = errors["confirmPassword"] || [];
  const answer = answers[question.id] || "";

  const handlePasswordCheck = async (password: string): Promise<void> => {
    const error = await validate(password);

    if (includes(error, "confirmPasswordMismatch")) {
      dispatch(
        setError({
          questionId: "confirmPassword",
          error: ["confirmPasswordMismatch"]
        })
      );
    } else dispatch(setError({ questionId: question.id, error }));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );

    handlePasswordCheck(e.target.value);
  };

  const handleConfirmPasswordChange = (
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    if (e.target.value !== answer) {
      dispatch(
        setError({
          questionId: "confirmPassword",
          error: ["confirmPasswordMismatch"]
        })
      );
    } else {
      dispatch(
        setError({
          questionId: question.id,
          error: null
        })
      );
      dispatch(
        setError({
          questionId: "confirmPassword",
          error: null
        })
      );
    }
  };

  return (
    <VStack gap={6}>
      <Field.Root
        id={`${question.id}`}
        required={question.required}
        disabled={status === "saving"}
        invalid={passwordError.length > 0}
        className={passwordError.length > 0 ? "ehqQuestionError" : null}
      >
        <Field.Label>
          <HTMLWrapper html={question.question} />
          {question.required && (
            <VisuallyHidden>{`* ${translate(
              "common.required"
            )}`}</VisuallyHidden>
          )}
        </Field.Label>
        <InputGroup
          endElement={
            <IconButton
              size="sm"
              fontSize="lg"
              variant="unstyled"
              display="inline-flex"
              onClick={setRevealPwd.toggle}
              data-testid="passwordQuestion-passwordToggle"
              icon={revealPwd ? <IoEyeOutline /> : <IoEyeOffOutline />}
              aria-label={
                revealPwd ? translate("common.hide") : translate("common.show")
              }
            />
          }
          endElementProps={{ width: 10, height: "100%" }}
        >
          <Input
            pr={10}
            value={answer}
            autoComplete="off"
            onChange={handleChange}
            data-testid="passwordQuestion-input"
            maxLength={question.maxLength}
            type={revealPwd ? "text" : "password"}
            size={{ base: "lg", lg: "md" }}
            name="passwordQuestion-input"
          />
        </InputGroup>
        {passwordError.length > 0 && (
          <Field.ErrorText data-testid="passwordQuestion-errorMessage">
            <Field.ErrorIcon />
            {passwordError
              .map((err) =>
                typeof err === "string"
                  ? translate(`errors.${err}`)
                  : translate(`errors.${err.key}`, err.values)
              )
              .join(", ")}
          </Field.ErrorText>
        )}
        <Field.HelperText data-testid="passwordQuestion-note">
          {translate("questions.password.note")}
        </Field.HelperText>
      </Field.Root>
      <Field.Root
        required={question.required}
        disabled={status === "saving"}
        invalid={confirmPasswordError.length > 0}
      >
        <Field.Label htmlFor="confirmPassword">
          {translate("questions.password.confirmPassword")}
          {question.required && (
            <VisuallyHidden>{`* ${translate(
              "common.required"
            )}`}</VisuallyHidden>
          )}
        </Field.Label>
        <InputGroup
          endElement={
            <IconButton
              size="sm"
              fontSize="lg"
              variant="unstyled"
              display="inline-flex"
              onClick={setRevealConfirmPwd.toggle}
              data-testid="confirmPasswordQuestion-passwordToggle"
              icon={revealConfirmPwd ? <IoEyeOutline /> : <IoEyeOffOutline />}
              aria-label={
                revealConfirmPwd
                  ? translate("common.hide")
                  : translate("common.show")
              }
            />
          }
          endElementProps={{ width: 10, height: "100%" }}
        >
          <Input
            id="confirmPassword"
            type={revealConfirmPwd ? "text" : "password"}
            autoComplete="off"
            data-testid="confirmPasswordQuestion-input"
            maxLength={question.maxLength}
            onChange={handleConfirmPasswordChange}
            size={{ base: "lg", lg: "md" }}
            name="confirmPasswordQuestion-input"
          />
        </InputGroup>
        {confirmPasswordError.length > 0 && (
          <Field.ErrorText data-testid="confirmPasswordQuestion-errorMessage">
            <Field.ErrorIcon />
            {translate(`errors.${confirmPasswordError}`)}
          </Field.ErrorText>
        )}
      </Field.Root>
    </VStack>
  );
};

export default PasswordQuestion;
